import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import React, { Suspense, useEffect } from "react";
import "./App.scss";

// Components
import Contact from "@components/Contact/Contact.tsx";
import Experience from "@components/Experience/Experience.tsx";
import Footer from "@components/Footer/Footer.tsx";
import HamburgerMenu from "@components/HamburgerMenu/HamburgerMenu.tsx";
import Hero from "@/components/Hero/Hero";
import LanguageToggle from "@components/LanguageToggle/LanguageToggle.tsx";
import Skills from "@components/Skills/Skills.tsx";
import SpaceBackground from "@components/SpaceBackground/SpaceBackground.tsx";
import LoadingScreen from "@components/LoadingScreen/LoadingScreen.tsx";

// Context and Data
import { experience, skills } from "./lib/Data/personalData.ts";
import {
  LanguageProvider,
  useLanguage,
} from "./lib/LanguageContext/LanguageContext.tsx";
import LanguageSyncAdapter from "./lib/LanguageContext/LanguageSyncAdapter.tsx";

// Hooks
import { useSEO } from "./hooks/useSEO";
import { useSceneLoading } from "./hooks/useSceneLoading.ts";

// Icons
import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Store
import { useLoadingStore } from "./lib/LoadingStore/loadingStore.ts";

const AppContent: React.FC = React.memo(() => {
  const { currentLang, t } = useLanguage();
  // t is used in child components

  // Initialize SEO with current language
  useSEO(undefined, currentLang);
  useSceneLoading();

  // Log welcome message in the appropriate language
  console.log(t("welcomeMessage"));

  const scrollToTop = React.useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    window.history.pushState(null, "", window.location.pathname);
  }, []);

  // Update viewport dimensions with throttling for better performance
  useEffect(() => {
    const updateDimensions = () => {
      // Get the actual viewport width without scrollbar
      const vw = document.documentElement.clientWidth * 0.01;
      document.documentElement.style.setProperty("--vw", `${vw}px`);

      // Update height as well
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    // Throttle resize events to improve performance
    let resizeTimeout: NodeJS.Timeout;
    const throttledUpdateDimensions = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateDimensions, 100);
    };

    updateDimensions();
    window.addEventListener("resize", throttledUpdateDimensions, {
      passive: true,
    });
    window.addEventListener("orientationchange", throttledUpdateDimensions, {
      passive: true,
    });

    return () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      window.removeEventListener("resize", throttledUpdateDimensions);
      window.removeEventListener(
        "orientationchange",
        throttledUpdateDimensions
      );
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = currentLang;
  }, [currentLang]);

  return (
    <div className="App">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <SpaceBackground />
      <main id="main-content" className="main-container">
        <nav className="navigation-container">
          <div className="nav-content">
            <button
              className="home-button"
              onClick={scrollToTop}
              aria-label="Scroll to top"
            >
              <FontAwesomeIcon icon={faHome} />
            </button>
            <div className="toggle-container">
              <LanguageToggle />
            </div>
            <HamburgerMenu />
          </div>
        </nav>
        <div className="section-wrapper">
          <section id="header" aria-label="Introduction and hero section">
            <Hero />
          </section>
          {/* <section id="education" aria-label="Educational background">
            <Education educationList={education} />
          </section> */}
          <section id="experience" aria-label="Work experience">
            <Experience jobs={experience} />
          </section>
          <section id="skills" aria-label="Technical skills">
            <Skills skills={skills} />
          </section>
          <section id="contact" aria-label="Contact information and form">
            <Contact />
          </section>
        </div>
        <Footer />
      </main>
    </div>
  );
});

const App: React.FC = () => {
  const isLoading = useLoadingStore((state) => state.isLoading);

  return (
    <>
      {isLoading && <LoadingScreen />}
      <Suspense fallback={null}>
        <LanguageProvider>
          <LanguageSyncAdapter />
          <AppContent />
        </LanguageProvider>
      </Suspense>
      <Analytics />
      <SpeedInsights />
    </>
  );
};

export default App;
